import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import api from "../../api/axios";
import { Mail, Lock, Eye, EyeOff, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "react-hot-toast";
import { preloadAdminPages } from "../../routes/adminPagePreload";

const Login = () => {
    const navigate = useNavigate();
    const { fetchAdmin } = useAdminAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    // ================= SUBMIT =================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) return;

        setLoading(true);

        try {
            const loginRes = await api.post("/api/auth/login", { email, password });
            if (loginRes.data?.token) {
                localStorage.setItem("admin_token", loginRes.data.token);
            }

            const res = await fetchAdmin();

            if (!res || !res.user) {
                throw new Error("Auth verification failed");
            }

            toast.success("Welcome back, Admin!");
            navigate("/admin/dashboard");
            preloadAdminPages();
        } catch (err) {
            console.error("Login failed:", err);
            let msg = err.response?.data?.message || "Invalid credentials. Please try again.";
            if (!err.response && (err.code === "ERR_NETWORK" || err.message === "Network Error")) {
                msg =
                    "Cannot reach the API server. Start the backend: cd admin-panel/backend && npm run dev";
            }
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] px-4">
            
            {/* DECORATIVE BACKGROUND ELEMENTS */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-[var(--accent-primary)]/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-500/5 rounded-full blur-[120px]" />
            </div>

            <div className="w-full max-w-md">
                
                {/* BRANDING */}
                <div className="flex flex-col items-center mb-8">
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight text-center">Savrion admin portal</h1>
                    <p className="text-slate-500 mt-2 font-medium">Manage your workforce with ease</p>
                </div>

                {/* LOGIN CARD */}
                <div className="bg-[var(--card-bg)] rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-100 p-8 md:p-10">
                    
                    <div className="mb-8">
                        <h2 className="text-xl font-bold text-slate-800">Welcome Back</h2>
                        <p className="text-slate-400 text-sm mt-1">Please enter your admin credentials</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* EMAIL */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 ml-1">Email Address</label>
                            <div className="relative group">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-500 transition-colors">
                                    <Mail size={18} />
                                </div>
                                <input
                                    type="email"
                                    placeholder="admin@savrion.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-12 pr-4 py-4 bg-[var(--bg-secondary)] border border-slate-200 rounded-2xl outline-none focus:border-[var(--accent-primary)] focus:bg-[var(--card-bg)] focus:ring-4 focus:ring-[var(--accent-primary)]/8 text-slate-800 transition-all placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        {/* PASSWORD */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 ml-1">Password</label>
                            <div className="relative group">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-500 transition-colors">
                                    <Lock size={18} />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-12 pr-12 py-4 bg-[var(--bg-secondary)] border border-slate-200 rounded-2xl outline-none focus:border-[var(--accent-primary)] focus:bg-[var(--card-bg)] focus:ring-4 focus:ring-[var(--accent-primary)]/8 text-slate-800 transition-all placeholder:text-slate-400"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* SUBMIT BUTTON */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#00c896] hover:bg-[#00b085] text-white font-bold py-4 rounded-2xl shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed group"
                        >
                            {loading ? (
                                "Authenticating..."
                            ) : (
                                <>
                                    Log In to Dashboard
                                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-slate-50 text-center">
                        <div className="inline-flex items-center gap-2 text-slate-400 text-xs font-medium uppercase tracking-wider">
                            <ShieldCheck size={14} className="text-emerald-500" />
                            Secure Admin Access Only
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <p className="text-center mt-8 text-slate-400 text-sm">
                    &copy; {new Date().getFullYear()} Savrion Technologies. All rights reserved.
                </p>
            </div>
        </div>
    );
};

export default Login;
